import React from 'react';
import styled, { css } from 'styled-components';
import {
  standardColours,
  brandColours,
  standardTransition,
  fontWeights,
  minBreakpointQuery,
  maxBreakpointQuery,
  elementColours,
  fontSize,
} from '../styles';
import { Container, Link } from './ui';
import MegaMenuCta from './MegaMenuCta';

const StyledMegaMenu = styled.div`
  padding-top: 20px;
  padding-bottom: 20px;
  background-color: ${brandColours.tertiary};

  ${minBreakpointQuery.large`
    padding-top: 40px;
    padding-bottom: 80px;
    border-top: 1px solid ${standardColours.lighterGrey};
    border-bottom: 1px solid ${standardColours.lighterGrey};
  `}
`;

const StyledInner = styled.div`
  display: grid;
  gap: 20px;

  ${minBreakpointQuery.small`
    gap: 30px;
  `}

  ${minBreakpointQuery.large`
    grid-template-columns: 0.7fr 0.3fr;
    align-items: start;
    gap: 120px;
  `}
`;

const StyledList = styled.ul`
  ${minBreakpointQuery.large`
    column-count: 3;
    column-gap: 40px;
  `}
`;

const StyledItem = styled.li`
  ${fontSize(14)};
  text-align: left;

  ${maxBreakpointQuery.large`
    margin-top: 5px;
  `}

  &:first-child {
    ${maxBreakpointQuery.large`
      margin-top: 0;
    `}
  }

  ${({ isPrimary }) => {
    if (isPrimary) {
      return css`
        break-inside: avoid;
        page-break-inside: avoid;
        -webkit-column-break-inside: avoid;
        margin-bottom: 10px;

        ${minBreakpointQuery.large`
          margin-bottom: 20px;
        `}
      `;
    }
  }}

  ${({ isSubItem }) => {
    if (isSubItem) {
      return css`
        font-weight: ${fontWeights.regular};
      `;
    } else {
      return css`
        font-weight: ${fontWeights.bold};
      `;
    }
  }}

  ${({ isBack }) => {
    if (isBack) {
      return css`
        ${minBreakpointQuery.large`
          display: none;          
        `}
      `;
    }
  }}
`;

const StyledLink = styled(Link)`
  transition: ${standardTransition('color')};

  ${maxBreakpointQuery.large`
    display: block;
    padding-top: 8px;
    padding-bottom: 8px;
  `}

  &:hover {
    color: ${brandColours.secondary};
  }

  ${({ isBack }) => {
    if (isBack) {
      return css`
        ${maxBreakpointQuery.large`
          position: relative;
          padding-left: 22px;
          padding-right: 0;
          background: none;
          border: none;
        `}

        &:before {
          ${maxBreakpointQuery.large`
            content: '';
            position: absolute;
            top: calc(50% + 1px);
            left: 2px;
            display: block;
            height: 9px;
            width: 9px;
            border: 2px solid ${elementColours.textColour};
            border-top: none;
            border-right: none;
            border-radius: 1px;
            transform: translateY(-50%) rotate(45deg);
            transition: ${standardTransition('border-color')};
          `}
        }
      `;
    }
  }}

  ${({ isViewAll }) => {
    if (isViewAll) {
      return css`
        text-decoration: underline;
        text-underline-offset: 3px;
      `;
    }
  }}
`;

const StyledSubItemList = styled.ul`
  margin-top: 10px;

  ${minBreakpointQuery.large`
    display: grid;
    gap: 10px;
    margin-top: 20px;
    margin-bottom: 40px;
  `}
`;

const StyledCtas = styled.div`
  display: grid;
  gap: 30px;
`;

const MegaMenu = ({
  items,
  viewAllLink,
  ctas,
  isMegaMenuOpen,
  setMegaMenuOpen,
  ...props
}) =>
  items.length > 0 && (
    <StyledMegaMenu {...props}>
      <Container wide={true}>
        <StyledInner>
          <StyledList>
            <StyledItem isBack={true}>
              <StyledLink
                as="button"
                isBack={true}
                onClick={() => setMegaMenuOpen(!isMegaMenuOpen)}
              >
                Back
              </StyledLink>
            </StyledItem>
            {items.map((node, id) => {
              const { textOverride, text, treeChildren } = node;

              return (
                <StyledItem key={id} isPrimary={true}>
                  <StyledLink to={node} isPrimary={true}>
                    {textOverride || text}
                  </StyledLink>
                  {treeChildren.length > 0 && (
                    <StyledSubItemList>
                      {treeChildren.map((node, id) => {
                        const { textOverride, text } = node;

                        return (
                          <StyledItem key={id} isSubItem={true}>
                            <StyledLink to={node}>
                              {textOverride || text}
                            </StyledLink>
                          </StyledItem>
                        );
                      })}
                    </StyledSubItemList>
                  )}
                </StyledItem>
              );
            })}
            <StyledItem isPrimary={true}>
              <StyledLink to={viewAllLink} isViewAll={true} isPrimary={true}>
                View all
              </StyledLink>
            </StyledItem>
          </StyledList>
          {ctas.length > 0 && (
            <StyledCtas>
              {ctas.map((cta, id) => (
                <MegaMenuCta key={id} {...cta} />
              ))}
            </StyledCtas>
          )}
        </StyledInner>
      </Container>
    </StyledMegaMenu>
  );

export default MegaMenu;
